import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Store, Plus, Search, Edit2, Trash2, Key, Mail, Phone, MapPin,
  Loader2, Check, X, Eye, EyeOff, BarChart3, Users, Gift, FileText,
  ChevronRight, Building2, Calendar, Clock, CheckCircle2, XCircle, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const CATEGORIES = [
  { value: 'gastronomia', label: 'Gastronomía', color: 'text-orange-400 bg-orange-500/10' },
  { value: 'entretenimiento', label: 'Entretenimiento', color: 'text-purple-400 bg-purple-500/10' },
  { value: 'servicios', label: 'Servicios', color: 'text-blue-400 bg-blue-500/10' },
  { value: 'retail', label: 'Retail', color: 'text-pink-400 bg-pink-500/10' },
  { value: 'salud', label: 'Salud y Bienestar', color: 'text-green-400 bg-green-500/10' },
  { value: 'educacion', label: 'Educación', color: 'text-cyan-400 bg-cyan-500/10' },
  { value: 'otros', label: 'Otros', color: 'text-gray-400 bg-gray-500/10' },
];

const TABS = [
  { id: 'partners', label: 'Partners Activos', icon: Store },
  { id: 'applications', label: 'Solicitudes', icon: FileText },
];

const PartnersAdminPage = () => {
  const [partners, setPartners] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('partners');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showApplicationDetail, setShowApplicationDetail] = useState(false);
  
  const [formData, setFormData] = useState({
    business_name: '',
    username: '',
    email: '',
    password: '',
    phone: '',
    address: '',
    category: 'gastronomia',
    logo_url: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [partnersRes, appsRes] = await Promise.all([
        api.get('/partners/admin/all'),
        api.get('/partners/admin/applications').catch(() => ({ data: [] }))
      ]);
      setPartners(partnersRes.data);
      setApplications(appsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingPartner) {
        await api.put(`/partners/admin/${editingPartner.id}`, {
          business_name: formData.business_name,
          phone: formData.phone,
          address: formData.address,
          category: formData.category,
          logo_url: formData.logo_url || null
        });
        toast.success('Partner actualizado');
      } else {
        await api.post('/partners/admin/create', formData);
        toast.success('Partner creado exitosamente');
      }
      
      setShowModal(false);
      setEditingPartner(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (partnerId) => {
    if (!window.confirm('¿Estás seguro de eliminar este partner?')) return;
    
    try {
      await api.delete(`/partners/admin/${partnerId}`);
      toast.success('Partner eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleResetPassword = async (partnerId) => {
    const newPassword = window.prompt('Ingresa la nueva contraseña (mín. 8 caracteres):');
    if (!newPassword || newPassword.length < 8) {
      if (newPassword) toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    
    try {
      await api.post(`/partners/admin/${partnerId}/reset-password?new_password=${encodeURIComponent(newPassword)}`);
      toast.success('Contraseña actualizada');
    } catch (error) {
      toast.error('Error al actualizar contraseña');
    }
  };

  const handleUpdateApplicationStatus = async (appId, status) => {
    try {
      await api.put(`/partners/admin/applications/${appId}?status=${status}`);
      toast.success(status === 'approved' ? 'Solicitud aprobada' : 'Solicitud rechazada');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const resetForm = () => {
    setFormData({
      business_name: '',
      username: '',
      email: '',
      password: '',
      phone: '',
      address: '',
      category: 'gastronomia',
      logo_url: ''
    });
  };

  const openEditModal = (partner) => {
    setEditingPartner(partner);
    setFormData({
      business_name: partner.business_name,
      username: partner.username || '',
      email: partner.email || '',
      password: '',
      phone: partner.phone || '',
      address: partner.address || '',
      category: partner.category || 'gastronomia',
      logo_url: partner.logo_url || ''
    });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingPartner(null);
    resetForm();
    setShowModal(true);
  };

  const filteredPartners = partners.filter(p => 
    p.business_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.username || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const pendingApps = applications.filter(a => a.status === 'pending');
  const getCategoryStyle = (cat) => CATEGORIES.find(c => c.value === cat) || CATEGORIES[6];

  if (loading) {
    return (
      <SidebarProvider>
        <AdminLayout>
          <div className="flex items-center justify-center h-64">
            <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
          </div>
        </AdminLayout>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <AdminLayout>
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-4 lg:p-6 space-y-6"
        >
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-black uppercase tracking-tight flex items-center gap-3">
                <Store className="text-[#D4AF37]" size={28} />
                Partners
              </h1>
              <p className="text-[#71717A] text-sm mt-1">Gestiona los negocios asociados</p>
            </div>
            <Button
              onClick={openCreateModal}
              className="bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-black font-bold hover:shadow-[0_0_20px_rgba(212,175,55,0.3)]"
            >
              <Plus size={18} className="mr-2" />
              Nuevo Partner
            </Button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-[#121212] border border-[#27272A] rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                  <Store className="text-[#D4AF37]" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{partners.length}</p>
                  <p className="text-[#71717A] text-xs">Partners</p>
                </div>
              </div>
            </div>
            <div className="bg-[#121212] border border-[#27272A] rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                  <Clock className="text-yellow-400" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-yellow-400">{pendingApps.length}</p>
                  <p className="text-[#71717A] text-xs">Pendientes</p>
                </div>
              </div>
            </div>
            <Link to="/admin/aprobar-beneficios" className="bg-[#121212] border border-[#27272A] rounded-xl p-4 hover:border-[#D4AF37]/50 transition-colors group">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <Gift className="text-purple-400" size={20} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-white group-hover:text-[#D4AF37] transition-colors">Beneficios</p>
                  <p className="text-[#71717A] text-xs">Por aprobar</p>
                </div>
                <ChevronRight size={16} className="text-[#52525B] group-hover:text-[#D4AF37] transition-colors" />
              </div>
            </Link>
            <div className="bg-[#121212] border border-[#27272A] rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <CheckCircle2 className="text-green-400" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-400">{partners.filter(p => p.is_active !== false).length}</p>
                  <p className="text-[#71717A] text-xs">Activos</p>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="bg-[#121212] border border-[#27272A] rounded-xl overflow-hidden">
            {/* Tabs */}
            <div className="flex border-b border-[#27272A]">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-semibold uppercase tracking-wider whitespace-nowrap transition-all relative ${
                    activeTab === tab.id
                      ? 'text-[#D4AF37] bg-[#D4AF37]/5'
                      : 'text-[#71717A] hover:text-white hover:bg-white/5'
                  }`}
                >
                  <tab.icon size={18} />
                  {tab.label}
                  {tab.id === 'applications' && pendingApps.length > 0 && (
                    <span className="ml-2 px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                      {pendingApps.length}
                    </span>
                  )}
                  {activeTab === tab.id && (
                    <motion.div
                      layoutId="partnerTabIndicator"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D4AF37]"
                    />
                  )}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="p-6">
              <AnimatePresence mode="wait">
                {activeTab === 'partners' && (
                  <motion.div
                    key="partners"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="space-y-4"
                  >
                    {/* Search */}
                    <div className="relative max-w-md">
                      <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
                      <Input
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Buscar por nombre o usuario..."
                        className="pl-10 h-11 bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#D4AF37]"
                      />
                    </div>

                    {/* Partners List */}
                    {filteredPartners.length === 0 ? (
                      <div className="text-center py-12">
                        <Store size={48} className="mx-auto text-[#27272A] mb-4" />
                        <p className="text-[#71717A]">No hay partners registrados</p>
                      </div>
                    ) : (
                      <div className="grid gap-4">
                        {filteredPartners.map((partner, index) => (
                          <motion.div
                            key={partner.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.03 }}
                            className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-4 hover:border-[#3F3F46] transition-colors"
                          >
                            <div className="flex items-center gap-4">
                              {/* Logo */}
                              {partner.logo_url ? (
                                <img src={partner.logo_url} alt="" className="w-14 h-14 rounded-xl object-cover bg-[#121212]" />
                              ) : (
                                <div className="w-14 h-14 rounded-xl bg-[#121212] flex items-center justify-center">
                                  <Building2 className="text-[#3F3F46]" size={24} />
                                </div>
                              )}

                              {/* Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3">
                                  <h3 className="font-bold text-white truncate">{partner.business_name}</h3>
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getCategoryStyle(partner.category).color}`}>
                                    {getCategoryStyle(partner.category).label}
                                  </span>
                                </div>
                                <div className="flex items-center gap-4 mt-1 text-sm text-[#71717A]">
                                  <span className="flex items-center gap-1">
                                    <span className="text-[#52525B]">@</span>{partner.username}
                                  </span>
                                  {partner.email && (
                                    <span className="flex items-center gap-1 truncate">
                                      <Mail size={12} />{partner.email}
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Actions */}
                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleResetPassword(partner.id)}
                                  className="text-[#71717A] hover:text-white hover:bg-white/10"
                                  title="Resetear contraseña"
                                >
                                  <Key size={16} />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openEditModal(partner)}
                                  className="text-[#71717A] hover:text-[#D4AF37] hover:bg-[#D4AF37]/10"
                                  title="Editar"
                                >
                                  <Edit2 size={16} />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDelete(partner.id)}
                                  className="text-[#71717A] hover:text-red-400 hover:bg-red-500/10"
                                  title="Eliminar"
                                >
                                  <Trash2 size={16} />
                                </Button>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </motion.div>
                )}

                {activeTab === 'applications' && (
                  <motion.div
                    key="applications"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="space-y-4"
                  >
                    {pendingApps.length === 0 ? (
                      <div className="text-center py-12">
                        <CheckCircle2 size={48} className="mx-auto text-green-500/50 mb-4" />
                        <p className="text-[#71717A]">No hay solicitudes pendientes</p>
                      </div>
                    ) : (
                      <div className="grid gap-4">
                        {pendingApps.map((app, index) => (
                          <motion.div
                            key={app.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="bg-[#0A0A0A] border border-yellow-500/30 rounded-xl p-5"
                          >
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                              <div className="flex-1">
                                <h3 className="font-bold text-white text-lg">{app.business_name}</h3>
                                <div className="flex flex-wrap gap-3 mt-2 text-sm text-[#A1A1AA]">
                                  <span className="flex items-center gap-1"><Mail size={14} /> {app.email}</span>
                                  {app.phone && <span className="flex items-center gap-1"><Phone size={14} /> {app.phone}</span>}
                                </div>
                                {app.message && (
                                  <p className="mt-3 text-[#71717A] text-sm bg-[#121212] p-3 rounded-lg line-clamp-2">"{app.message}"</p>
                                )}
                                {app.form_responses?.length > 0 && (
                                  <p className="mt-2 text-xs text-[#D4AF37]">
                                    {app.form_responses.length} campos adicionales en el formulario
                                  </p>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <Button
                                  onClick={() => {
                                    setSelectedApplication(app);
                                    setShowApplicationDetail(true);
                                  }}
                                  variant="outline"
                                  className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                                  data-testid={`view-detail-${app.id}`}
                                >
                                  <Eye size={16} className="mr-2" />
                                  Ver Detalle
                                </Button>
                                <Button
                                  onClick={() => handleUpdateApplicationStatus(app.id, 'rejected')}
                                  className="bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/30"
                                >
                                  <XCircle size={16} className="mr-2" />
                                  Rechazar
                                </Button>
                                <Button
                                  onClick={() => handleUpdateApplicationStatus(app.id, 'approved')}
                                  className="bg-green-500/10 text-green-400 hover:bg-green-500/20 border border-green-500/30"
                                >
                                  <CheckCircle2 size={16} className="mr-2" />
                                  Aprobar
                                </Button>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Create/Edit Modal */}
          <Dialog open={showModal} onOpenChange={setShowModal}>
            <DialogContent className="bg-[#121212] border border-[#27272A] max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl font-black uppercase tracking-tight flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#D4AF37] to-[#F59E0B] rounded-lg flex items-center justify-center">
                    <Store size={20} className="text-black" />
                  </div>
                  {editingPartner ? 'Editar Partner' : 'Nuevo Partner'}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-sm font-medium text-[#A1A1AA] mb-2 block">Nombre del Negocio *</Label>
                    <Input
                      value={formData.business_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, business_name: e.target.value }))}
                      placeholder="Bar La Esquina"
                      required
                      className="h-12 bg-[#18181B] border-[#27272A] text-white focus:border-[#D4AF37]"
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-[#A1A1AA] mb-2 block">Usuario *</Label>
                    <Input
                      value={formData.username}
                      onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                      placeholder="bar_laesquina"
                      required
                      className="h-12 bg-[#18181B] border-[#27272A] text-white focus:border-[#D4AF37]"
                    />
                    {editingPartner && (
                      <p className="text-xs text-[#52525B] mt-1">⚠️ Cambiar el usuario afectará el login del partner</p>
                    )}
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-[#A1A1AA] mb-2 block">Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="contacto@bar.com"
                      className="h-12 bg-[#18181B] border-[#27272A] text-white focus:border-[#D4AF37]"
                    />
                  </div>

                  {!editingPartner && (
                    <div>
                      <Label className="text-sm font-medium text-[#A1A1AA] mb-2 block">Contraseña *</Label>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={formData.password}
                          onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                          placeholder="••••••••"
                          required
                          className="h-12 bg-[#18181B] border-[#27272A] text-white pr-12 focus:border-[#D4AF37]"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-[#52525B] hover:text-white transition-colors"
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>
                  )}

                  <div>
                    <Label className="text-sm font-medium text-[#A1A1AA] mb-2 block">Categoría</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                    >
                      <SelectTrigger className="h-12 bg-[#18181B] border-[#27272A] text-white focus:border-[#D4AF37]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#27272A]">
                        {CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-[#A1A1AA] mb-2 block">Teléfono</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="+54 11 1234-5678"
                      className="h-12 bg-[#18181B] border-[#27272A] text-white focus:border-[#D4AF37]"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label className="text-sm font-medium text-[#A1A1AA] mb-2 block">Dirección</Label>
                    <Input
                      value={formData.address}
                      onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                      placeholder="Av. Corrientes 1234"
                      className="h-12 bg-[#18181B] border-[#27272A] text-white focus:border-[#D4AF37]"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label className="text-sm font-medium text-[#A1A1AA] mb-2 block">Logo URL</Label>
                    <Input
                      value={formData.logo_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, logo_url: e.target.value }))}
                      placeholder="https://..."
                      className="h-12 bg-[#18181B] border-[#27272A] text-white focus:border-[#D4AF37]"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-[#27272A]">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowModal(false)}
                    className="text-[#A1A1AA] hover:text-white"
                  >
                    Cancelar
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={saving} 
                    className="bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-black font-bold hover:shadow-[0_0_20px_rgba(212,175,55,0.3)]"
                  >
                    {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                    {editingPartner ? 'Actualizar' : 'Crear Partner'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Application Detail Modal */}
          <Dialog open={showApplicationDetail} onOpenChange={setShowApplicationDetail}>
            <DialogContent className="bg-[#121212] border border-[#27272A] max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl font-black uppercase tracking-tight flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#D4AF37] to-[#F59E0B] rounded-lg flex items-center justify-center">
                    <FileText size={20} className="text-black" />
                  </div>
                  Detalle de Solicitud
                </DialogTitle>
              </DialogHeader>
              
              {selectedApplication && (
                <div className="space-y-6 mt-4">
                  {/* Basic Info */}
                  <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-4">
                    <h3 className="text-lg font-bold text-white mb-3">{selectedApplication.business_name}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="flex items-center gap-2 text-[#A1A1AA]">
                        <Mail size={14} className="text-[#D4AF37]" />
                        <span>{selectedApplication.email || 'No proporcionado'}</span>
                      </div>
                      {selectedApplication.phone && (
                        <div className="flex items-center gap-2 text-[#A1A1AA]">
                          <Phone size={14} className="text-[#D4AF37]" />
                          <span>{selectedApplication.phone}</span>
                        </div>
                      )}
                      {selectedApplication.category && (
                        <div className="flex items-center gap-2 text-[#A1A1AA]">
                          <Store size={14} className="text-[#D4AF37]" />
                          <span className="capitalize">{selectedApplication.category}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-[#A1A1AA]">
                        <Calendar size={14} className="text-[#D4AF37]" />
                        <span>{new Date(selectedApplication.created_at).toLocaleString('es-AR')}</span>
                      </div>
                    </div>
                    {selectedApplication.message && (
                      <div className="mt-4 p-3 bg-[#121212] rounded-lg">
                        <p className="text-xs text-[#71717A] mb-1">Mensaje:</p>
                        <p className="text-[#A1A1AA] text-sm">{selectedApplication.message}</p>
                      </div>
                    )}
                  </div>

                  {/* Form Responses */}
                  {selectedApplication.form_responses?.length > 0 && (
                    <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-4">
                      <h4 className="text-sm font-bold text-[#D4AF37] uppercase tracking-wider mb-4">
                        Respuestas del Formulario
                      </h4>
                      <div className="space-y-4">
                        {selectedApplication.form_responses.map((response, index) => (
                          <div key={index} className="border-b border-[#27272A] pb-3 last:border-0 last:pb-0">
                            <p className="text-xs text-[#71717A] mb-1">{response.field_label || response.field_id}</p>
                            <p className="text-white text-sm whitespace-pre-wrap">
                              {response.field_type === 'checkbox' 
                                ? (response.value ? '✓ Sí' : '✗ No')
                                : (response.value || '-')}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3 pt-4 border-t border-[#27272A]">
                    <Button
                      onClick={() => {
                        handleUpdateApplicationStatus(selectedApplication.id, 'rejected');
                        setShowApplicationDetail(false);
                      }}
                      className="flex-1 bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/30"
                    >
                      <XCircle size={16} className="mr-2" />
                      Rechazar
                    </Button>
                    <Button
                      onClick={() => {
                        handleUpdateApplicationStatus(selectedApplication.id, 'approved');
                        setShowApplicationDetail(false);
                      }}
                      className="flex-1 bg-green-500/10 text-green-400 hover:bg-green-500/20 border border-green-500/30"
                    >
                      <CheckCircle2 size={16} className="mr-2" />
                      Aprobar
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </motion.div>
      </AdminLayout>
    </SidebarProvider>
  );
};

export default PartnersAdminPage;
